import React from 'react';
import { Drawer, Row, Col, Card, Typography, Divider, Button, Space, Spin } from 'antd';
import type { ClientOverviewResponse, ClientOverviewEntityStats } from '../../services/api';
import type { MismatchedEntityType } from '../../services/api';

interface DiagnosisOverviewModalProps {
  open: boolean;
  onClose: () => void;
  data?: ClientOverviewResponse;
  loading?: boolean;
  onViewWithoutCoordinates: (type: MismatchedEntityType) => void;
  locationLabel?: string;
}

const entityMeta: Record<MismatchedEntityType, { label: string; color: string }> = {
  clients: { label: 'Clients', color: '#3b82f6' },
  vendors: { label: 'Vendors', color: '#8b5cf6' },
  prospects: { label: 'Prospects', color: '#f97316' },
};

const formatNumber = (value?: number) => {
  if (typeof value !== 'number') {
    return '0';
  }
  return value.toLocaleString();
};

const OverviewCard: React.FC<{
  type: MismatchedEntityType;
  stats?: ClientOverviewEntityStats;
  onAction: (type: MismatchedEntityType) => void;
}> = ({ type, stats, onAction }) => {
  const meta = entityMeta[type];
  const hasMissing = (stats?.without_coordinates ?? 0) > 0;

  return (
    <Card
      bordered={false}
      style={{
        background: '#f8fafc',
        borderRadius: 12,
        minHeight: 200,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Space>
          <span
            style={{
              display: 'inline-flex',
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: meta.color,
            }}
          />
          <Typography.Text strong style={{ color: '#0f172a' }}>
            {meta.label}
          </Typography.Text>
        </Space>

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            With Coordinates
          </Typography.Text>
          <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>
            {formatNumber(stats?.with_coordinates)}
          </Typography.Title>
        </div>

        <div>
          <Space align="baseline" size={8}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Without Coordinates
              </Typography.Text>
              <Typography.Title level={4} style={{ margin: 0, color: hasMissing ? '#dc2626' : '#0f172a' }}>
                {formatNumber(stats?.without_coordinates)}
              </Typography.Title>
            </div>
            <Button
              size="small"
              type="link"
              disabled={!hasMissing}
              onClick={() => onAction(type)}
              style={{ paddingLeft: 0 }}
            >
              Fix Missing
            </Button>
          </Space>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Total Records
          </Typography.Text>
          <Typography.Title level={5} style={{ margin: 0, color: '#0f172a' }}>
            {formatNumber(stats?.total)}
          </Typography.Title>
        </div>
      </Space>
    </Card>
  );
};

const DiagnosisOverviewModal: React.FC<DiagnosisOverviewModalProps> = ({
  open,
  onClose,
  data,
  loading,
  onViewWithoutCoordinates,
  locationLabel,
}) => {
  return (
    <Drawer
      title={
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            Data Diagnostics
          </Typography.Text>
          {locationLabel && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {locationLabel}
            </Typography.Text>
          )}
        </div>
      }
      placement="right"
      width={1200}
      onClose={onClose}
      open={open}
      destroyOnClose
      maskClosable
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {(Object.keys(entityMeta) as MismatchedEntityType[]).map((type) => (
              <Col span={8} key={type}>
                <OverviewCard type={type} stats={data?.[type]} onAction={onViewWithoutCoordinates} />
              </Col>
            ))}
            <Col span={24}>
              <Card
                bordered={false}
                style={{
                  background: '#ecfccb',
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                }}
              >
                <Space direction="vertical" size={8}>
                  <Typography.Text strong style={{ color: '#365314' }}>
                    Summary
                  </Typography.Text>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        With Coordinates
                      </Typography.Text>
                      <Typography.Title level={4} style={{ margin: 0, color: '#365314' }}>
                        {formatNumber(data?.summary?.total_with_coordinates)}
                      </Typography.Title>
                    </div>
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Without Coordinates
                      </Typography.Text>
                      <Typography.Title level={4} style={{ margin: 0, color: '#dc2626' }}>
                        {formatNumber(data?.summary?.total_without_coordinates)}
                      </Typography.Title>
                    </div>
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Grand Total
                      </Typography.Text>
                      <Typography.Title level={4} style={{ margin: 0, color: '#365314' }}>
                        {formatNumber(data?.summary?.grand_total)}
                      </Typography.Title>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Drawer>
  );
};

export default DiagnosisOverviewModal;
